import React, { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useUploader } from "../hooks/use-uploader";
import { API, BACKEND_URL } from "../assets/staticData";
import { createSlug } from "../utils/slug";
import ClearCacheButton from "../components/ClearCacheButton/ClearCacheButton";
import { CACHE_KEY } from "../constant/cache-key";

const STATUSES = [
  { label: "Active", value: "ACTIVE" },
  { label: "Pending", value: "PENDING" },
  { label: "Expired", value: "EXPIRED" },
];

const DEFAULT_BUTTONS = Array.from({ length: 6 }, (_, index) => ({
  label: `Button ${index + 1}`,
  url: "",
  isEnabled: true,
}));

const createPromotion = (order = 0) => ({
  id: null,
  title: "",
  description: "",
  status: "ACTIVE",
  slug: "",
  link: "",
  isVisible: true,
  image: null,
  imageName: "",
  order,
});

const ContestMarketing = () => {
  const [expandedPromotion, setExpandedPromotion] = useState(0);
  const [savingPromotionIndexes, setSavingPromotionIndexes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSavingButtons, setIsSavingButtons] = useState(false);
  const { upload, isLoading } = useUploader();

  const { control, watch, setValue, reset } = useForm({
    defaultValues: {
      buttons: [],
      promotions: [],
    },
  });

  const { fields: buttonFields } = useFieldArray({
    control,
    name: "buttons",
  });

  const { fields: promotionFields, append, remove, move } = useFieldArray({
    control,
    name: "promotions",
  });

  const watchButtons = watch("buttons");
  const watchPromotions = watch("promotions");

  // Get Contest View (Buttons)
  const fetchContestView = async () => {
    try {
      let userToken = localStorage.getItem("userToken");
      const headers = {
        authorization: `Bearer ${userToken}`,
      };

      const res = await fetch(`${API}/contest-view?populate=buttons`, {
        headers,
        method: "GET",
      });

      const { data } = await res.json();
      setValue("buttons", data.attributes.buttons || [], { shouldDirty: false });
    } catch (error) {
      console.log(error)
    }
  }

  // Update Contest View (Buttons)
  const updateContestView = async ({ buttons }) => {
    try {
      let userToken = localStorage.getItem("userToken");
      const headers = {
        "Content-Type": "application/json",
        authorization: `Bearer ${userToken}`,
      };

      const res = await fetch(`${API}/contest-view`, {
        headers,
        method: "PUT",
        body: JSON.stringify({ data: { buttons } })
      })

      const { data } = await res.json();
      reset({
        buttons: buttons,
        promotions: watchPromotions
      })
      return buttons
    } catch (error) {
      console.log(error)
      return null
    }
  }

  // Get Contest Promotions
  const fetchPromotions = async () => {
    try {
      let userToken = localStorage.getItem("userToken");
      const headers = {
        authorization: `Bearer ${userToken}`,
      };

      const res = await fetch(`${API}/contest-promotions?populate=image&sort=order:asc`, {
        headers,
        method: "GET",
      });

      const { data } = await res.json();
      const promotions = (data || [])
        .map((item, index) => {
          const attrs = item.attributes || {};
          const imageData = attrs.image?.data;
          const image =
            imageData && imageData.attributes
              ? {
                id: imageData.id,
                url: imageData.attributes.url,
                name: imageData.attributes.name,
                ...imageData.attributes,
              }
              : null;

          return {
            id: item.id,
            title: attrs.title || "",
            description: attrs.description || "",
            status: attrs.status || "ACTIVE",
            slug: attrs.slug || "",
            link: attrs.link || "",
            isVisible: attrs.isVisible ?? true,
            image,
            imageName: image?.name || "",
            order: attrs.order ?? index,
          };
        });

      setValue("promotions", promotions, { shouldDirty: false });
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPromotions(), fetchContestView()]);
      setLoading(false);
    };

    loadData();
  }, []);

  const saveButtons = async () => {
    setIsSavingButtons(true);

    try {
      const result = await updateContestView({ buttons: watchButtons })

      if (result) {
        toast(`Saved ${watchButtons?.length || 0} button(s).`, { type: "success" });
      } else {
        toast("Unable to save buttons.", { type: "error" });
      }
    } catch (error) {
      console.error(error);
      toast("Unable to save buttons.", { type: "error" });
    } finally {
      setIsSavingButtons(false);
    }
  };

  const handlePromotionDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const updatedPromotions = [...watchPromotions];
    const [moved] = updatedPromotions.splice(result.source.index, 1);
    updatedPromotions.splice(result.destination.index, 0, moved);

    move(result.source.index, result.destination.index);

    updatedPromotions.forEach((_, idx) => {
      setValue(`promotions.${idx}.order`, idx, { shouldDirty: true });
    });
    toast("Promotion order updated locally. Save changes to persist.", { type: "info" });
  };

  const savePromotion = async (index, showMessage = true) => {
    const promotion = watchPromotions?.[index];
    if (!promotion) return false;

    setSavingPromotionIndexes((prev) => [...new Set([...prev, index])]);

    const payload = {
      title: promotion.title,
      description: promotion.description,
      status: promotion.status,
      slug: promotion.slug,
      link: promotion.link,
      isVisible: promotion.isVisible,
      order: promotion.order ?? index,
      image: promotion.image?.id || null,
    };

    const endpoint = promotion.id
      ? `${API}/contest-promotions/${promotion.id}`
      : `${API}/contest-promotions`;
    const method = promotion.id ? "PUT" : "POST";
    let success = false;

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
        },
        body: JSON.stringify({ data: payload }),
      });

      const responseData = await response.json();

      if (response.ok && responseData?.data) {
        const savedData = responseData.data;
        const returnedId = savedData.id || promotion.id;

        const attrs = savedData.attributes || {};
        const imageData = attrs.image?.data;
        const savedImage =
          imageData && imageData.attributes
            ? {
              id: imageData.id,
              url: imageData.attributes.url,
              name: imageData.attributes.name,
              ...imageData.attributes,
            }
            : promotion.image;

        setValue(
          `promotions.${index}`,
          {
            id: returnedId,
            title: attrs.title ?? promotion.title,
            description: attrs.description ?? promotion.description,
            status: attrs.status ?? promotion.status,
            slug: attrs.slug ?? promotion.slug,
            link: attrs.link ?? promotion.link,
            isVisible: attrs.isVisible ?? promotion.isVisible,
            image: savedImage,
            imageName: savedImage?.name || promotion.imageName || "",
            order: attrs.order ?? promotion.order ?? index,
          },
          { shouldDirty: false }
        );

        if (showMessage) {
          toast(`Promotion ${index + 1} saved successfully.`, { type: "success" });
        }
        success = true;
      } else {
        const message =
          responseData?.error?.message || responseData?.message || `Unable to save promotion ${index + 1}.`;
        if (showMessage) {
          toast(message, { type: "error" });
        }
        console.error("Promotion save failed", responseData);
      }
    } catch (error) {
      if (showMessage) {
        toast(`Unable to save promotion ${index + 1}.`, { type: "error" });
      }
      console.error(error);
    } finally {
      setSavingPromotionIndexes((prev) => prev.filter((item) => item !== index));
    }

    return success;
  };

  const deletePromotion = async (index) => {
    const promotion = watchPromotions?.[index];
    if (!promotion) return;

    if (promotion.id) {
      try {
        const response = await fetch(`${API}/contest-promotions/${promotion.id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          console.error("Promotion delete failed", errorData);
          toast(`Unable to delete promotion ${index + 1}.`, { type: "error" });
          return;
        }
      } catch (error) {
        console.error(error);
        toast(`Unable to delete promotion ${index + 1}.`, { type: "error" });
        return;
      }
    }

    const updatedPromotions = [...watchPromotions];
    updatedPromotions.splice(index, 1);
    remove(index);

    updatedPromotions.forEach((_, idx) => {
      setValue(`promotions.${idx}.order`, idx, { shouldDirty: true });
    });

    toast(`Promotion ${index + 1} deleted.`, { type: "success" });
    if (expandedPromotion === index) {
      setExpandedPromotion(false);
    }
  };

  const saveAllPromotions = async () => {
    const promotionCount = watchPromotions?.length || 0;
    if (!promotionCount) return;

    let savedCount = 0;
    let failedCount = 0;

    for (let index = 0; index < promotionCount; index += 1) {
      const saved = await savePromotion(index, false);
      if (saved) {
        savedCount += 1;
      } else {
        failedCount += 1;
      }
    }

    toast(
      `${savedCount} promotion(s) saved successfully.${failedCount ? ` ${failedCount} failed to save.` : ""}`,
      { type: failedCount ? "warning" : "success" }
    );
  };

  const handleImageUpload = async (index, file) => {
    if (!file) {
      setValue(`promotions.${index}.image`, null, { shouldDirty: true });
      setValue(`promotions.${index}.imageName`, "", { shouldDirty: true });
      return;
    }

    const imageObject = await upload(file);
    if (imageObject) {
      setValue(`promotions.${index}.image`, imageObject, { shouldDirty: true });
      setValue(`promotions.${index}.imageName`, file.name, { shouldDirty: true });
    }
  };

  const removePromotionImage = (index) => {
    setValue(`promotions.${index}.image`, null, { shouldDirty: true });
    setValue(`promotions.${index}.imageName`, "", { shouldDirty: true });
  };

  const autoGenerateSlug = (index, title) => {
    const slug = title ? createSlug(title) : "";
    setValue(`promotions.${index}.slug`, slug, { shouldDirty: true });
  };

  const promotionNotes = useMemo(
    () => ({
      ACTIVE: "Active promotions: use a large hero image, recommended 435x735px.",
      PENDING: "Pending promotions: use a large hero image, recommended 435x735px.",
      EXPIRED: "Expired promotions: use a small hero image, recommended 300x384.",
    }),
    []
  );

  if (loading) {
    return (
      <Box sx={{ width: "100%", minHeight: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Typography variant="h1" fontSize={18} fontWeight={"700"} mb={2}>
        Contest View And Promotion Settings
      </Typography>

      <ClearCacheButton
        keys={[
          CACHE_KEY.contestPromotions,
          CACHE_KEY.contestView
        ]} />

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1}>
          Action Buttons
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Define fixed button content, destination link and visibility for buttons 1 through 6.
        </Typography>

        <Grid container spacing={2}>
          {buttonFields.map((field, index) => (
            <Grid item xs={12} md={6} key={field.id}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1">{field.name}</Typography>

                  <Controller
                    name={`buttons.${index}.label`}
                    control={control}
                    rules={{ required: "Label is required" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Label"
                        size="small"
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  <Controller
                    name={`buttons.${index}.url`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Link URL"
                        size="small"
                        fullWidth
                        placeholder="https://example.com"
                      />
                    )}
                  />

                  {/* <Controller
                    name={`buttons.${index}.isEnabled`}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label={field.value ? "Enabled" : "Disabled"}
                      />
                    )}
                  /> */}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button variant="contained" onClick={saveButtons} disabled={isSavingButtons}>
            {isSavingButtons ? "Saving buttons..." : "Save Buttons"}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h6">Promotions</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => {
              const nextIndex = promotionFields.length;
              append(createPromotion(nextIndex));
              setExpandedPromotion(nextIndex);
            }}
          >
            Add Promotion
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Manage promotion cards with status, slug, image upload and ordering. Drag a promotion to reorder it.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" size="small" onClick={saveAllPromotions} disabled={!promotionFields.length || savingPromotionIndexes.length > 0}>
            {savingPromotionIndexes.length > 0 ? "Saving all..." : "Save All Promotions"}
          </Button>
        </Box>

        <DragDropContext onDragEnd={handlePromotionDragEnd}>
          <Droppable droppableId="promotions-droppable">
            {(provided) => (
              <Stack spacing={2} ref={provided.innerRef} {...provided.droppableProps}>
                {promotionFields.map((field, index) => {

                  const currentPromotion = watchPromotions?.[index] || {};

                  const previewUrl = currentPromotion?.image?.url
                    ? currentPromotion.image.url.startsWith("http")
                      ? currentPromotion.image.url
                      : `${BACKEND_URL}${currentPromotion.image.url}`
                    : null;

                  return (
                    <Draggable key={field.id} draggableId={field.id} index={index}>
                      {(provided, snapshot) => (
                        <Accordion
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          expanded={expandedPromotion === index}
                          onChange={() => setExpandedPromotion(expandedPromotion === index ? false : index)}
                          sx={{
                            borderColor: snapshot.isDragging ? "primary.main" : "divider",
                            backgroundColor: snapshot.isDragging ? "rgba(25, 118, 210, 0.08)" : "background.paper",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            {...provided.dragHandleProps}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <DragIndicatorIcon fontSize="small" />
                              <Box>
                                <Typography variant="subtitle1">
                                  Promotion {index + 1}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {currentPromotion.title || "Untitled promotion"}
                                </Typography>
                              </Box>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
                              <FormControlLabel
                                control={
                                  <Controller
                                    name={`promotions.${index}.isVisible`}
                                    control={control}
                                    render={({ field }) => <Switch {...field} checked={field.value} />}
                                  />
                                }
                                label={currentPromotion?.isVisible ? "Visible" : "Hidden"}
                              />
                              <IconButton color="error" onClick={() => deletePromotion(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Controller
                                  name={`promotions.${index}.title`}
                                  control={control}
                                  rules={{ required: "Title is required" }}
                                  render={({ field, fieldState }) => (
                                    <TextField
                                      {...field}
                                      label="Promotion Title"
                                      size="small"
                                      fullWidth
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      onBlur={(event) => {
                                        field.onBlur();
                                        if (!currentPromotion?.slug) {
                                          autoGenerateSlug(index, event.target.value);
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Controller
                                  name={`promotions.${index}.status`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                      <InputLabel id={`promotion-status-label-${index}`}>Status</InputLabel>
                                      <Select
                                        {...field}
                                        labelId={`promotion-status-label-${index}`}
                                        label="Status"
                                      >
                                        {STATUSES.map((option) => (
                                          <MenuItem value={option.value} key={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Controller
                                  name={`promotions.${index}.slug`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      label="Slug"
                                      size="small"
                                      fullWidth
                                      InputProps={{
                                        endAdornment: (
                                          <Button size="small" onClick={() => autoGenerateSlug(index, currentPromotion?.title || "")}>Auto</Button>
                                        ),
                                      }}
                                    />
                                  )}
                                />
                                <FormHelperText sx={{ ml: 0, mt: 0.5 }}>
                                  Use the slug base from title or edit manually.
                                </FormHelperText>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Controller
                                  name={`promotions.${index}.link`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      label="Promotion Link"
                                      size="small"
                                      fullWidth
                                      placeholder="https://example.com/promotion"
                                    />
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Controller
                                  name={`promotions.${index}.description`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      label="Description"
                                      size="small"
                                      fullWidth
                                      multiline
                                      rows={3}
                                    />
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                  <input
                                    accept="image/*"
                                    id={`promotion-image-${index}`}
                                    type="file"
                                    hidden
                                    onChange={async (event) => {
                                      const file = event.target.files?.[0] ?? null;
                                      await handleImageUpload(index, file);
                                    }}
                                  />
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <label htmlFor={`promotion-image-${index}`}>
                                      <Button component="span" variant="outlined" startIcon={<UploadFileIcon />} size="small" disabled={isLoading}>
                                        {isLoading ? "Uploading..." : "Upload Photo"}
                                      </Button>
                                    </label>
                                    {previewUrl && (
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => removePromotionImage(index)}
                                      >
                                        Remove Image
                                      </Button>
                                    )}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {currentPromotion?.imageName || "No image uploaded."}
                                  </Typography>
                                  {previewUrl && (
                                    <Box component="img" src={previewUrl} alt="Promotion preview" sx={{ width: 120, height: 80, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {promotionNotes[currentPromotion?.status] || promotionNotes.DEFAULT}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ mt: 2, alignSelf: "flex-start" }}
                                    onClick={() => savePromotion(index)}
                                    disabled={savingPromotionIndexes.includes(index)}
                                  >
                                    {savingPromotionIndexes.includes(index) ? "Saving..." : currentPromotion?.id ? "Update Promotion" : "Save Promotion"}
                                  </Button>
                                </Box>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </Paper>
      {/* 
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={() => reset()}>
          Reset
        </Button>
      </Box> */}

    </Box>
  );
};

export default ContestMarketing;